import { cn } from "@/utils/styles";
import { BalanceType } from "@/types/user";
import { AuthMachineContext } from "@/store/auth";
import { useEffect, useMemo } from "react";
import { trackEvent } from "@/utils/analytics";
import { AnalyticsEventType } from "@/constants/analytics";
import env from "@/env";
import { useExplorerOnlyMode } from "@/hooks/useExplorerOnlyMode";
import { useUserStore } from "@/store/user";
import { toLocaleString } from "@/utils/number";
import { useAccountBurnRate } from "@/hooks/useAccountBurnRate";
import { formatPaygRunway } from "@/utils/payg";
import { ChevronDown } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Link } from "wouter";

type CreditsFieldProps = {
  className?: string;
  onPurchaseCredits?: () => void;
};

const formatCreditAmount = (value: number | null) =>
  typeof value === "number" ? `$${toLocaleString(value, 2)}` : "—";

const PopoverRow = ({
  label,
  value,
  valueClassName,
  isTotal
}: {
  label: string;
  value: string;
  valueClassName?: string;
  isTotal?: boolean;
}) => (
  <div
    className={cn(
      "flex items-center justify-between gap-3 text-[13px] leading-[18px]",
      isTotal && "mt-1 border-t border-border pt-2"
    )}
  >
    <span className={isTotal ? "font-medium text-foreground" : "text-muted-foreground"}>
      {label}
    </span>
    <span className={cn("font-medium tabular-nums text-foreground", valueClassName)}>{value}</span>
  </div>
);

/** Runway colours: red once clusters are hours from stopping, yellow within a day. */
const RUNWAY_TEXT_CLASS = {
  ok: undefined,
  low: "text-yellow-light-400",
  critical: "text-red-light-400"
} as const;

export const CreditsField = ({ className, onPurchaseCredits }: CreditsFieldProps) => {
  const isLoggedIn = AuthMachineContext.useSelector((state) => state.context.isLoggedIn);
  const userId = useUserStore((store) => store.userId);
  const balances = useUserStore((store) => store.balances);
  const fetchBalances = useUserStore((store) => store.fetchBalances);

  const { totalBalance, purchasedBalance, promotionalBalance } = useMemo(() => {
    const total = balances?.[BalanceType.CREDITS];
    const purchased = balances?.[BalanceType.CREDITS_WITHDRAWABLE];
    const totalNum = typeof total === "number" ? total : null;
    const purchasedNum = typeof purchased === "number" ? purchased : null;
    const promotionalNum =
      totalNum !== null && purchasedNum !== null
        ? Math.max(0, totalNum - purchasedNum)
        : totalNum !== null
          ? 0
          : null;

    return {
      totalBalance: totalNum,
      purchasedBalance: purchasedNum,
      promotionalBalance: promotionalNum
    };
  }, [balances]);

  // Mounted app-wide, so it reads the burn rate without polling for it.
  const burnRate = useAccountBurnRate();

  useEffect(() => {
    if (!isLoggedIn || !userId) {
      return;
    }
    void fetchBalances(true);
  }, [fetchBalances, isLoggedIn, userId]);

  const explorerOnlyMode = useExplorerOnlyMode();

  if (!isLoggedIn) {
    return null;
  }

  if (explorerOnlyMode) {
    return null;
  }

  const balanceReady =
    totalBalance !== null || purchasedBalance !== null || promotionalBalance !== null;

  return (
    <div
      className={cn(
        "flex items-center gap-[8px] rounded-[8px] border-[1.25px] border-border bg-background px-[12px] py-[4px]",
        className
      )}
    >
      <div className="relative size-[20px] shrink-0 overflow-clip">
        <img
          alt="Credits"
          className="absolute inset-0 block size-full max-w-none"
          src="/assets/images/figma/topbar-credits-icon.svg"
        />
      </div>
      <span className="shrink-0 whitespace-nowrap text-[14px] font-medium leading-[20px] text-foreground">
        Credits Balance
      </span>
      <div className="flex items-center gap-[4px]">
        <HoverCard openDelay={150} closeDelay={100}>
          <HoverCardTrigger asChild>
            <button
              type="button"
              className={cn(
                "flex items-center gap-[2px] rounded-[6px] border-[1.25px] border-border bg-background px-[6px] py-[2px]",
                "text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              )}
              aria-label="Credits balance breakdown"
            >
              {balanceReady && (
                <span className="whitespace-nowrap text-[12px] font-medium leading-[16px]">
                  {formatCreditAmount(totalBalance)}
                </span>
              )}
              <ChevronDown className="size-[12px] shrink-0 text-muted-foreground" aria-hidden />
            </button>
          </HoverCardTrigger>
          <HoverCardContent
            align="end"
            sideOffset={8}
            className="w-[220px] border-border bg-background p-3 shadow-md"
          >
            <div className="flex flex-col gap-2">
              <PopoverRow label="Paid credits" value={formatCreditAmount(purchasedBalance)} />
              <PopoverRow
                label="Promotional credits"
                value={formatCreditAmount(promotionalBalance)}
              />
              <PopoverRow label="Total" value={formatCreditAmount(totalBalance)} isTotal />
            </div>
          </HoverCardContent>
        </HoverCard>
        {burnRate.isVisible && (
          <HoverCard openDelay={150} closeDelay={100}>
            <HoverCardTrigger asChild>
              <Link
                href="/cloud/id/funds"
                aria-label={`View billing history. Current burn rate $${toLocaleString(burnRate.hourlyRate, 2)} per hour`}
                className={cn(
                  "flex items-center justify-center rounded-[6px] border-[1.25px] border-border bg-background px-[6px] py-[2px]",
                  "text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  burnRate.runwayLevel === "low" && "border-yellow-light-400",
                  burnRate.runwayLevel === "critical" && "border-red-light-400"
                )}
              >
                <span
                  className={cn(
                    "whitespace-nowrap text-[12px] font-medium leading-[16px] text-foreground",
                    RUNWAY_TEXT_CLASS[burnRate.runwayLevel]
                  )}
                  style={{ fontFeatureSettings: "'cv09' 1, 'ss11' 1, 'calt' 0, 'liga' 0" }}
                >
                  ${toLocaleString(burnRate.hourlyRate, 2)}/hr
                </span>
              </Link>
            </HoverCardTrigger>
            <HoverCardContent
              align="end"
              sideOffset={8}
              className="w-[220px] border-border bg-background p-3 shadow-md"
            >
              <div className="flex flex-col gap-2">
                <PopoverRow label="Running clusters" value={`${burnRate.clusterCount}`} />
                <PopoverRow
                  label="Burn rate"
                  value={`$${toLocaleString(burnRate.hourlyRate, 2)}/hr`}
                />
                <PopoverRow
                  label="Est. monthly"
                  value={`$${toLocaleString(burnRate.monthlyEstimate, 2)}`}
                />
                <PopoverRow
                  label="Runway"
                  value={formatPaygRunway(burnRate.runwayHours)}
                  valueClassName={RUNWAY_TEXT_CLASS[burnRate.runwayLevel]}
                  isTotal
                />
              </div>
            </HoverCardContent>
          </HoverCard>
        )}
        <button
          type="button"
          aria-label="Purchase credits"
          className="flex items-center justify-center rounded-[6px] border-[1.25px] border-border bg-background p-[2px] transition-colors hover:bg-muted"
          onClick={() => {
            trackEvent(
              env.FEATUREFLAG_PURCHASE_CREDITS_ENABLED
                ? AnalyticsEventType.PURCHASE_CREDIT_CLICKED
                : AnalyticsEventType.REQUEST_CREDIT_CLICKED
            );
            onPurchaseCredits?.();
          }}
        >
          <div className="relative size-[16px] shrink-0 overflow-clip">
            <div className="absolute inset-[30%]">
              <img
                alt=""
                className="block size-full max-w-none"
                src="/assets/images/figma/topbar-credits-plus-icon.svg"
              />
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};
